import React, {
    createContext,
    FunctionComponent,
    ReactNode,
    useContext,
    useEffect,
    useLayoutEffect,
    useState
} from 'react';
import { AuthenticationProvider, LoadingStatus } from '@paycor/pacl';
import { AuthService, User } from './interfaces/AuthService';

/** This file is copied from
 * https://dev.azure.com/Paycor/Paycor%20Inc/_git/Paycor.Application.Component.Library?path=/src/AuthorizedRoute/AuthorizedRoute.js&_a=contents&version=GBmaster
 * as of Jan 4, 2023, and adapted to be a general component.
 * It essentially is a simple component that requires an authorized user.
 * It is a terrible component in that it is intimately tied into the React rendering loop when in reality it shouldn't be.
 */

/** Secured Route that only allows users who are authorized to proceed to the target path.  This only checks if the user
 * is authorized to see the application, but does not check for specific page-level privileges. */
const redirectOnUnauthenticated = (url?: string) => {
    const _return = encodeURIComponent(window.location.href);
    // needed to prevent endless loops
    if (!window.location.href.includes('/Accounts/Authentication/Signin')) {
        const target = url ?? window.location.origin;
        window.location.href = `${target}/Accounts/Authentication/Signin?ReturnUrl=${_return}`;
    }
};

export const UserContext = createContext<User>(null as any as User);

export const UserContextProvider: FunctionComponent<{ children: ReactNode | ReactNode[] }> = ({ children }) => {
    // Pull the AuthenticationService instance from the hoisted context
    const authService = useContext<AuthService>(AuthenticationProvider.Context);
    const [isAuthenticating, setAuthenticating] = useState(true);
    const [user, setUser] = useState<User | undefined>(undefined);

    // onMount, check to see if logged in
    useLayoutEffect(() => {
        const authUser = async () => {
            let u;
            try {
                u = await authService.getUser();
                if (!u) {
                    throw new Error('User not found');
                }
            } catch {
                // there was an error, maybe the user wasn't authenticated yet.
                console.log('An error occured trying to get user from authService, authenticate user');
                u = await authService.authenticate();
            }

            setUser(u);
        };

        authUser().catch(console.log);
    }, [authService]);

    // if the user is loaded, toggle off the loading state
    useEffect(() => {
        // turn off "isAuthenticating" if the user token has returned and has not expired
        if (isAuthenticating && user && !user.expired) {
            setAuthenticating(false);
        }
    }, [user, isAuthenticating]);

    // The user is authenticated, but is either expired or does not have permission
    if (!isAuthenticating && (!user || user.expired)) {
        redirectOnUnauthenticated();
        return null;
    }

    if (isAuthenticating || !user) {
        return <LoadingStatus />;
    }

    return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
};
